export const TUTOR_ONBOARDING_STEP_IDS = [
  "concept",
  "language",
  "level",
  "student-experience",
  "speaking-demo",
  "tutor-studio",
  "signup",
] as const;

export type TutorOnboardingStepId = (typeof TUTOR_ONBOARDING_STEP_IDS)[number];

export interface TutorOnboardingAnswers {
  languageCode?: string;
  languageName?: string;
  cefrLevel?: string;
}

export interface TutorOnboardingState {
  currentStep: number;
  answers: TutorOnboardingAnswers;
  startedAt: number;
}

export const TUTOR_CEFR_OPTIONS = [
  {
    id: "A1",
    label: "First conversations",
    description: "They can use a few familiar words and phrases.",
    shortcut: "A",
  },
  {
    id: "A2",
    label: "Simple exchanges",
    description: "They can handle short, familiar conversations.",
    shortcut: "B",
  },
  {
    id: "B1",
    label: "Everyday conversation",
    description: "They can keep talking, but still pause or get stuck.",
    shortcut: "C",
  },
  {
    id: "B2",
    label: "Growing confidence",
    description: "They want to sound more natural and precise.",
    shortcut: "D",
  },
  {
    id: "C1",
    label: "Advanced conversation",
    description: "They can discuss complex ideas with good control.",
    shortcut: "E",
  },
  {
    id: "C2",
    label: "Near-native range",
    description: "They want demanding, nuanced speaking practice.",
    shortcut: "F",
  },
] as const;

const ALLOWED_SOURCES = new Set(["direct", "tutors_page", "tutor_studio_empty"]);

export function normalizeTutorOnboardingSource(source: string | null | undefined): string {
  return source && ALLOWED_SOURCES.has(source) ? source : "direct";
}

export function getTutorDemoLevel(cefrLevel?: string): "beginner" | "intermediate" | "advanced" {
  if (cefrLevel === "B1" || cefrLevel === "B2") return "intermediate";
  if (cefrLevel === "C1" || cefrLevel === "C2") return "advanced";
  return "beginner";
}

export function parseStoredTutorOnboardingState(
  rawState: string | null,
  now = Date.now()
): TutorOnboardingState | null {
  if (!rawState) return null;

  try {
    const parsed = JSON.parse(rawState) as Partial<TutorOnboardingState>;
    if (!parsed.startedAt || now - parsed.startedAt > 1000 * 60 * 60 * 24 * 7) return null;

    const currentStep = Math.max(
      0,
      Math.min(Number(parsed.currentStep) || 0, TUTOR_ONBOARDING_STEP_IDS.length - 1)
    );

    return {
      currentStep,
      answers: parsed.answers && typeof parsed.answers === "object" ? parsed.answers : {},
      startedAt: parsed.startedAt,
    };
  } catch {
    return null;
  }
}
